import type { MessageState, MessageType } from "@baton/contracts";
import { agentNodeId, trafficEdgeId } from "./model.js";

/**
 * Transient activity that decorates an existing graph edge for a bounded interval.
 *
 * Messages now carry a project ID end to end (contracts, persisted rows, every list, get
 * and realtime authorization path, and the crypto-worker DTOs), and the server applies
 * the membership-plus-repository intersection before it emits an event, so live
 * project-scoped activity is on. The demo producer remains for tests and previews.
 */
export const LIVE_ACTIVITY_ENABLED = true;

export const ACTIVITY_STATES = ["queued", "opened", "acknowledged", "completed", "blocked", "failed"] as const;
export type ActivityState = (typeof ACTIVITY_STATES)[number];

export type ActivityPacketType = "handoff" | "report" | "wip" | "assignment" | "chat" | "activity";

/**
 * Routing metadata only. No subject, no body, no attachment name, no path. Content
 * stays ciphertext and is decrypted in the crypto worker when an authorized thread
 * is opened, never to drive an animation.
 */
export interface ActivityEvent {
  id: string;
  projectId: string;
  edgeId: string;
  packetType: ActivityPacketType;
  state: ActivityState;
  at: number;
  ttlMs: number;
}

export const ACTIVITY_TTL_MS = 6_000;
export const ACTIVITY_LIMIT = 6;

export function isExpired(event: ActivityEvent, now: number): boolean {
  return now - event.at >= event.ttlMs;
}

export function pruneActivity(events: readonly ActivityEvent[], now: number): ActivityEvent[] {
  return events.filter((event) => !isExpired(event, now));
}

/**
 * Coalesces bursts: one edge carries at most one live packet, and the whole canvas
 * carries at most ACTIVITY_LIMIT of them, so a noisy project cannot turn into an
 * unbounded animation queue.
 */
export function reduceActivity(
  events: readonly ActivityEvent[],
  incoming: ActivityEvent,
  now: number,
  limit: number = ACTIVITY_LIMIT,
): ActivityEvent[] {
  const kept = pruneActivity(events, now).filter((event) => event.edgeId !== incoming.edgeId);
  return [...kept, incoming].slice(-limit);
}

export interface ActivityPresentation {
  label: string;
  glyph: string;
  tone: "neutral" | "route" | "settled" | "blocked";
  travels: boolean;
  /** Fraction of the path the packet reaches, so "opened" visibly stalls mid-flight. */
  travelTo: number;
  durationMs: number;
}

/** State is carried by label, glyph and motion as well as colour, never by colour alone. */
export function describeActivity(state: ActivityState): ActivityPresentation {
  switch (state) {
    case "queued":
      return { label: "queued", glyph: "→", tone: "route", travels: true, travelTo: 1, durationMs: 1_400 };
    case "opened":
      return { label: "opened", glyph: "◎", tone: "route", travels: true, travelTo: 0.62, durationMs: 1_800 };
    case "acknowledged":
      return { label: "acknowledged", glyph: "✓", tone: "route", travels: true, travelTo: 0.78, durationMs: 1_800 };
    case "completed":
      return { label: "completed", glyph: "✔", tone: "settled", travels: true, travelTo: 1, durationMs: 900 };
    case "blocked":
      return { label: "blocked", glyph: "!", tone: "blocked", travels: false, travelTo: 0.5, durationMs: 0 };
    case "failed":
      return { label: "failed", glyph: "×", tone: "blocked", travels: false, travelTo: 0.5, durationMs: 0 };
  }
}

/** The routing-only payload the API attaches to every `message.*` outbox event. */
export interface RealtimeMessageEvent {
  id: string;
  topic: string;
  messageId: string;
  type: MessageType;
  projectId: string | null;
  senderAgentId: string;
  recipientAgentIds: string[];
  state?: MessageState;
  agentId?: string;
  at: number;
  /** Decrypted completion outcome when the merged message already carries one. */
  outcome?: "success" | "partial" | "blocked" | "failed" | null;
}

export interface ActivityContext {
  projectId: string;
  nodeIds: ReadonlySet<string>;
  /** Existing wire edge ids keyed by `${sourceAgentId}|${targetAgentId}`. */
  wireEdges: ReadonlyMap<string, string>;
}

function stateForTopic(event: RealtimeMessageEvent): ActivityState | null {
  switch (event.topic) {
    case "message.created": return "queued";
    case "message.opened": return "opened";
    case "message.acknowledged": return "acknowledged";
    case "message.completed":
      return event.outcome === "blocked" ? "blocked" : event.outcome === "failed" ? "failed" : "completed";
    default: return null;
  }
}

function packetTypeFor(type: MessageType): ActivityPacketType {
  return type;
}

/**
 * One packet per recipient. It rides the wire between sender and recipient when one
 * exists (either direction), otherwise a transient traffic edge; recipients whose node is
 * not on this canvas are skipped rather than re-parented.
 */
export function activityFromRealtime(event: RealtimeMessageEvent, context: ActivityContext): ActivityEvent[] {
  const state = stateForTopic(event);
  if (!state || event.projectId !== context.projectId) return [];
  const senderNode = agentNodeId(event.senderAgentId);
  if (!context.nodeIds.has(senderNode)) return [];
  const recipients = event.agentId ? [event.agentId] : event.recipientAgentIds;
  const packets: ActivityEvent[] = [];
  for (const recipientId of recipients) {
    const recipientNode = agentNodeId(recipientId);
    if (!context.nodeIds.has(recipientNode) || recipientId === event.senderAgentId) continue;
    const edgeId = context.wireEdges.get(`${event.senderAgentId}|${recipientId}`)
      ?? context.wireEdges.get(`${recipientId}|${event.senderAgentId}`)
      ?? trafficEdgeId(senderNode, recipientNode);
    packets.push({
      id: `${event.id}:${recipientId}`,
      projectId: context.projectId,
      edgeId,
      packetType: packetTypeFor(event.type),
      state,
      at: event.at,
      ttlMs: ACTIVITY_TTL_MS,
    });
  }
  return packets;
}

const DEMO_SEQUENCE: ActivityState[] = ["queued", "opened", "acknowledged", "completed", "blocked"];

/**
 * Demonstration packet for previews and tests. It exercises the edge animation, its TTL
 * and its reduced-motion fallback without touching the network.
 */
export function createDemoActivityEvent(input: {
  projectId: string;
  edgeId: string;
  step: number;
  now: number;
}): ActivityEvent {
  const state = DEMO_SEQUENCE[input.step % DEMO_SEQUENCE.length] ?? "queued";
  return {
    id: `demo:${input.edgeId}:${input.step}`,
    projectId: input.projectId,
    edgeId: input.edgeId,
    packetType: state === "completed" ? "report" : "handoff",
    state,
    at: input.now,
    ttlMs: ACTIVITY_TTL_MS,
  };
}
